import type { GameClient } from "../net/GameClient";

/**
 * DamageVignette — red fade on the screen edges when the local player
 * takes damage. Intensity scales with damage delta; decays over 500 ms.
 *
 * Pure CSS overlay (a fixed full-screen `<div>` with an inset
 * radial-gradient mask producing a vignette ring). Driven entirely from
 * `GameClient.onHit` — no per-frame update needed; the CSS `transition`
 * carries the decay.
 */
export class DamageVignette {
  private readonly client: GameClient;
  private readonly root: HTMLDivElement;
  private readonly unsubs: Array<() => void> = [];
  private decayHandle = 0;

  /** Visible lifetime (ms) — matches the spec. */
  private static readonly DECAY_MS = 500;

  constructor(client: GameClient) {
    this.client = client;
    this.root = document.createElement("div");
    this.root.id = "damage-vignette";
    document.body.appendChild(this.root);
    this.injectStyles();

    this.unsubs.push(
      this.client.onHit((msg) => {
        if (msg.victimSessionId !== this.client.sessionId) return;
        this.flash(msg.damage);
      }),
    );
  }

  destroy(): void {
    window.clearTimeout(this.decayHandle);
    for (const u of this.unsubs) u();
    this.root.remove();
  }

  /**
   * Trigger one damage flash. Opacity scales from 0.25 (light tag) up to
   * 0.9 (lethal hit). Subsequent flashes stack to the higher value.
   */
  private flash(damage: number): void {
    const clamped = Math.max(0, Math.min(120, damage));
    const intensity = 0.25 + (clamped / 120) * 0.65;
    // Snap to peak, then transition fades back down.
    this.root.style.transition = "opacity 30ms linear";
    this.root.style.opacity = String(Math.max(intensity, parseFloat(this.root.style.opacity || "0")));
    window.clearTimeout(this.decayHandle);
    this.decayHandle = window.setTimeout(() => {
      this.root.style.transition = `opacity ${DamageVignette.DECAY_MS}ms ease-out`;
      this.root.style.opacity = "0";
    }, 60);
  }

  private injectStyles(): void {
    if (document.getElementById("damage-vignette-styles")) return;
    const style = document.createElement("style");
    style.id = "damage-vignette-styles";
    style.textContent = `
      #damage-vignette {
        position: fixed;
        inset: 0;
        pointer-events: none;
        z-index: 4;
        opacity: 0;
        /* Radial gradient: dark red ring fading toward the centre. */
        background: radial-gradient(ellipse at center,
          rgba(255, 0, 0, 0) 38%,
          rgba(255, 30, 30, 0.55) 78%,
          rgba(170, 0, 0, 0.92) 100%);
        mix-blend-mode: screen;
      }
    `;
    document.head.appendChild(style);
  }
}
